// =============================================================================
// ui/tabs/ReadinessGate.tsx — the pre-market checklist.
// -----------------------------------------------------------------------------
// Three groups of four, ticked as the trader works through their prep. The pill
// at the top of the tab reflects it live, which is the whole point: it is the
// answer to "did I complete my own pre-flight".
//
// ── SAVES ON EVERY TICK, like the old client ──
//
// Beta's checkbox handler mutates and calls `scheduleAutosave()`. There is no
// Save button on this section in beta's screenshot and adding one would be a
// control it does not have. So each tick issues its own 4-column upsert —
// small, and realistically spread over the minutes a trader spends on prep.
//
// ⚠ OPTIMISTIC, AND IT REVERTS. The box flips immediately so the checklist feels
// like a checklist, but a failed write puts it back and says why. A tick that
// silently did not persist is worse than a slow one: the trader believes their
// pre-flight is recorded.
//
// ── THE GEAR IS NOT HERE ──
//
// Beta has a gear that adds, renames and removes items. It edits
// `user_preferences.readiness_groups` — a GLOBAL list, not this day's ticks — so
// it belongs to a settings surface and writes a different table. Named on screen
// rather than left as a missing icon.
// =============================================================================

import { useState } from 'react';
import { JournalSection } from './JournalSection';
import { useSupabase } from '../state/supabase';
import { saveReadiness } from '../../io/sync/saveReadiness';
import {
  applyReadinessToggle, isReadinessComplete, isTicked, readinessProgress,
  type ReadinessGroup,
} from '../../selectors/readiness';

export interface ReadinessGateProps {
  items: Readonly<Record<string, unknown>>;
  completedAt: string | null;
  /** ⚠ From `user_preferences`, never a constant. See domain/readiness.ts. */
  groups: readonly ReadinessGroup[];
  date: string;
  baselineUpdatedAt: string | null;
  onSaved: (
    items: Record<string, boolean>, completedAt: string | null, updatedAt: string | null,
  ) => void;
}

export function ReadinessGate(
  { items, completedAt, groups, date, baselineUpdatedAt, onSaved }: ReadinessGateProps,
) {
  const { sb, userId } = useSupabase();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const complete = isReadinessComplete(items, groups);
  const { done, total } = readinessProgress(items, groups);

  const toggle = async (key: string, checked: boolean) => {
    setBusyKey(key);
    setError(null);
    const next = applyReadinessToggle(
      items, key, checked, groups, completedAt, new Date().toISOString(),
    );
    const res = await saveReadiness({
      sb, userId, date,
      items: next.items, completedAt: next.completedAt, baselineUpdatedAt,
    });
    setBusyKey(null);
    if (res.error) { setError(res.error); return; }
    onSaved(next.items, next.completedAt, res.updatedAt);
  };

  return (
    /**
     * ⚠ 🚨 A REAL SECTION CARD. This rendered `.jp-sec` with a bare `<h4>` and no
     * header row, so on the running page it was the one block in the pre-market
     * column with no icon badge and no collapse chevron — five cards and a
     * loose heading. Found by opening the page after fixing the same defect on
     * the Session and Recap phases, not by any test.
     *
     * ⚠ THE PROGRESS PILL IS THE HEADER'S `meta`, which is where beta puts this
     * kind of note. `✓` is beta's glyph for this section.
     */
    <JournalSection
      icon="✓" title="Readiness Gate"
      meta={complete ? 'Complete' : `${done} of ${total} ticked`}
    >
      <p className="muted">
        Pre-market checklist. Tick items as you complete them — the pill at the top of
        the tab reflects status live.
      </p>

      <div className="rg-grid">
        {groups.map((g) => (
          <div className="rg-group" key={g.key || g.label}>
            <h5>{g.label}</h5>
            <div className="checklist">
              {g.items.map((it) => {
                const on = isTicked(items, it.key);
                return (
                  <label key={it.key} className={on ? 'checked' : ''}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={busyKey !== null}
                      onChange={(e) => void toggle(it.key, e.target.checked)}
                    />
                    <span>{it.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {error ? <p className="red" role="alert">{error}</p> : null}

      {/* ⚠ NAMED, not a silently missing icon. */}
      <p className="muted">
        Adding, renaming and removing checklist items needs the gear in the current
        client — those are a global list shared by every day, not this day’s ticks.
      </p>
    </JournalSection>
  );
}
